import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

function formatApiErrorDetail(detail) {
    if (detail == null) return "Une erreur s'est produite. Veuillez réessayer.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
                withCredentials: true
            });
            setUser(response.data);
        } catch {
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/login`, 
                { email, password },
                { withCredentials: true }
            );
            setUser(response.data);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: formatApiErrorDetail(error.response?.data?.detail) 
            };
        }
    };

    const register = async (email, password, name, role = 'commercial') => {
        try {
            const response = await axios.post(`${API_URL}/api/auth/register`, 
                { email, password, name, role },
                { withCredentials: true }
            );
            setUser(response.data);
            return { success: true };
        } catch (error) {
            return { 
                success: false, 
                error: formatApiErrorDetail(error.response?.data?.detail) 
            };
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
        } catch (err) {
            // Server-side logout best-effort; client state is always cleared
            console.warn('Logout API failed:', err?.response?.status || err?.message);
        }
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, checkAuth }}>
            {children}
        </AuthContext.Provider>
    );
};
